import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Autoplay, EffectCoverflow } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { motion } from 'framer-motion';

import BusinessIcon from '@mui/icons-material/Business';
import StoreIcon from '@mui/icons-material/Store';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import LanguageIcon from '@mui/icons-material/Language';
import StarIcon from '@mui/icons-material/Star';

import { useProducer } from '../../../context/ProducerContext';
import { Producer } from '../../../models/Producer';

function Producers() {
  const [producers, setProducers] = useState<Producer[]>([]);
  const [activeProducer, setActiveProducer] = useState<Producer | null>(null);
  const [isLoadingProducers, setIsLoadingProducers] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const { getProducersWithImages } = useProducer();
  const navigate = useNavigate();

  // Wykrywanie urządzenia mobilnego
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);

    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  // Pobieranie producentów
  useEffect(() => {
    const fetchProducers = async () => {
      setIsLoadingProducers(true);
      try {
        const producersData = await getProducersWithImages();
        setProducers(producersData);
        if (producersData.length > 0) {
          setActiveProducer(producersData[0]);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoadingProducers(false);
      }
    };

    fetchProducers();
  }, [getProducersWithImages]);

  const handleSlideChange = (swiper: any) => {
    if (producers[swiper.realIndex]) {
      setActiveProducer(producers[swiper.realIndex]);
    }
  };

  // Animacje
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: 'easeOut',
      },
    },
  };

  // Placeholder dla braku producentów lub podczas ładowania
  if (isLoadingProducers) {
    return (
      <div className="py-16 w-full">
        <div className="flex justify-center items-center h-40">
          <div className="animate-pulse space-y-6 w-full max-w-4xl">
            <div className="h-8 bg-gray-200 rounded w-1/3 mx-auto"></div>
            <div className="h-2 bg-gray-200 rounded w-1/2 mx-auto"></div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, index) => (
                <div key={index} className="h-32 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (producers.length === 0) {
    return null;
  }

  return (
    <div className="w-full py-12 md:py-16 px-4 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto">
        {/* Nagłówek sekcji */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="text-center mb-10"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-black to-primary">
              Nasi producenci
            </span>
          </h2>
          <div className="mx-auto w-16 md:w-20 h-1.5 rounded-full mb-3 md:mb-4 bg-primary"></div>
          <p className="text-gray-600 text-sm md:text-base max-w-2xl mx-auto">
            Współpracujemy z najlepszymi producentami branży elektrycznej, aby
            zapewnić najwyższą jakość produktów
          </p>
        </motion.div>

        {/* Karuzela producentów - desktop i mobile */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInVariants}
          className="mb-12"
        >
          <Swiper
            slidesPerView={isMobile ? 2.2 : 5}
            centeredSlides={true}
            spaceBetween={isMobile ? 16 : 30}
            grabCursor={true}
            loop={true}
            effect="coverflow"
            coverflowEffect={{
              rotate: 10,
              stretch: 0,
              depth: 100,
              modifier: 1,
              slideShadows: false,
            }}
            autoplay={{
              delay: 3000,
              disableOnInteraction: true,
              pauseOnMouseEnter: true,
            }}
            modules={[Autoplay, EffectCoverflow]}
            onSlideChange={handleSlideChange}
            className="producersSwiper py-8 pb-16"
          >
            {producers.map((producer) => (
              <SwiperSlide
                key={producer.id}
                className="flex flex-col items-center justify-center"
              >
                <motion.div
                  whileHover={{
                    y: -5,
                    boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  }}
                  className="bg-white rounded-xl shadow-md p-6 flex items-center justify-center aspect-square max-w-[160px] transition-all duration-300 border border-gray-100"
                >
                  <Link
                    to={`/producent/${producer.name}`}
                    className="w-full h-full flex items-center justify-center"
                  >
                    <img
                      src={
                        producer.image ||
                        `https://placehold.co/200x200?text=${encodeURIComponent(producer.name)}`
                      }
                      alt={producer.name}
                      className="object-contain max-h-full max-w-full transition-all duration-300 hover:opacity-90"
                      style={{ filter: 'saturate(0.9)' }}
                    />
                  </Link>
                </motion.div>
                <span className="mt-3 text-sm font-medium text-gray-800">
                  {producer.name}
                </span>
              </SwiperSlide>
            ))}
          </Swiper>
        </motion.div>

        {/* Szczegóły aktywnego producenta */}
        {activeProducer && (
          <motion.div
            key={activeProducer.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="mb-16"
          >
            <div className="grid grid-cols-1 md:grid-cols-7 gap-6">
              {/* Lewa kolumna - logo i podstawowe info */}
              <motion.div
                variants={itemVariants}
                className="md:col-span-2 bg-white rounded-xl overflow-hidden shadow-md p-6 border border-gray-100 flex flex-col items-center text-center"
              >
                <div className="w-32 h-32 p-4 bg-white rounded-xl mb-6 flex items-center justify-center border border-gray-100">
                  <img
                    src={
                      activeProducer.image ||
                      `https://placehold.co/200x200?text=${encodeURIComponent(activeProducer.name)}`
                    }
                    alt={activeProducer.name}
                    className="max-h-full max-w-full object-contain"
                  />
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {activeProducer.name}
                </h3>

                <div className="space-y-3 w-full mb-6">
                  {activeProducer.foundedYear && (
                    <div className="flex justify-between items-center text-sm py-2 border-b border-gray-100">
                      <span className="text-gray-500">Rok założenia:</span>
                      <span className="font-medium text-gray-900">
                        {activeProducer.foundedYear}
                      </span>
                    </div>
                  )}

                  {activeProducer.headquarters && (
                    <div className="flex justify-between items-center text-sm py-2 border-b border-gray-100">
                      <span className="text-gray-500">Siedziba:</span>
                      <span className="font-medium text-gray-900">
                        {activeProducer.headquarters}
                      </span>
                    </div>
                  )}

                  {activeProducer.speciality && (
                    <div className="flex justify-between items-center text-sm py-2 border-b border-gray-100">
                      <span className="text-gray-500">Specjalizacja:</span>
                      <span className="font-medium text-gray-900">
                        {activeProducer.speciality}
                      </span>
                    </div>
                  )}
                </div>

                <motion.div className="mt-auto w-full" whileHover={{ y: -2 }}>
                  {activeProducer.website && (
                    <a
                      href={
                        activeProducer.website.startsWith('http')
                          ? activeProducer.website
                          : `https://${activeProducer.website}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-primary font-medium"
                    >
                      <LanguageIcon fontSize="small" />
                      <span>Odwiedź stronę WWW</span>
                    </a>
                  )}
                </motion.div>
              </motion.div>

              {/* Prawa kolumna - opis i produkty */}
              <motion.div
                variants={itemVariants}
                className="md:col-span-5 bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 flex flex-col"
              >
                <div className="p-6 border-b border-gray-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 rounded-full bg-primary/10 text-primary">
                      <BusinessIcon />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      O producencie
                    </h3>
                  </div>

                  <div className="prose prose-sm max-w-none text-gray-700">
                    {activeProducer.description ? (
                      <p>{activeProducer.description}</p>
                    ) : (
                      <p>
                        {activeProducer.name} to jeden z naszych zaufanych
                        partnerów. Oferujemy szeroki wybór produktów tej marki w
                        konkurencyjnych cenach.
                      </p>
                    )}

                    {activeProducer.history && (
                      <div className="mt-4">
                        <h4 className="text-base font-medium text-gray-900 mb-2">
                          Historia firmy
                        </h4>
                        <p className="text-sm">{activeProducer.history}</p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-grow">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="p-2 rounded-full bg-primary/10 text-primary">
                      <StoreIcon />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      Produkty {activeProducer.name}
                    </h3>
                  </div>

                  <div className="flex-grow flex items-center justify-center py-4">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center text-primary">
                        <StarIcon style={{ fontSize: '2rem' }} />
                      </div>
                      <p className="text-gray-600 mb-6 max-w-md">
                        Odkryj pełną ofertę produktów marki{' '}
                        {activeProducer.name} dostępnych w naszym sklepie
                      </p>

                      <motion.button
                        onClick={() =>
                          navigate(`/producent/${activeProducer.name}`)
                        }
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg font-medium transition-all duration-300 hover:bg-primary/90"
                        whileHover={{
                          scale: 1.05,
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                        }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <span>Zobacz produkty {activeProducer.name}</span>
                        <KeyboardArrowRightIcon fontSize="small" />
                      </motion.button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Siatka popularnych producentów */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
          className="mt-8"
        >
          <motion.div variants={itemVariants} className="mb-8">
            <h3 className="text-xl md:text-2xl font-bold mb-2 text-gray-900 text-center">
              Popularne marki
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Odkryj produkty od renomowanych producentów branżowych
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {producers.slice(0, 12).map((producer, index) => (
              <motion.div
                key={`grid-${producer.id}`}
                variants={itemVariants}
                whileHover={{
                  y: -3,
                  boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
                  borderColor: 'rgba(var(--color-primary), 0.5)',
                }}
                className="bg-white rounded-lg p-4 border border-gray-200 transition-all duration-300 flex items-center justify-center aspect-[4/2]"
              >
                <Link
                  to={`/producent/${producer.name}`}
                  className="w-full h-full flex items-center justify-center"
                >
                  <img
                    src={
                      producer.image ||
                      `https://placehold.co/200x100?text=${encodeURIComponent(producer.name)}`
                    }
                    alt={producer.name}
                    className="object-contain h-full max-w-full filter hover:brightness-110 transition-all"
                  />
                </Link>
              </motion.div>
            ))}
          </div>

          {producers.length > 12 && (
            <motion.div variants={itemVariants} className="mt-8 text-center">
              <Link to="/producenci">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-800 rounded-full font-medium hover:bg-gray-200 transition-colors"
                >
                  <span>Zobacz wszystkich producentów</span>
                  <KeyboardArrowRightIcon fontSize="small" />
                </motion.button>
              </Link>
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
}

export default Producers;
